import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { FieldWrapper } from '~/components/field-wrapper/FieldWrapper'
import { useFieldError } from '~/components/form/useFieldError'
import { useFormCustomContext } from '~/components/form/useFormCustomContext'
import { ValidationOptions } from '~/components/form/validation'

import type { NumberInputProps } from '../number-input/NumberInput'
import { NumberInput } from '../number-input/NumberInput'

export interface NumberInputFieldProps extends NumberInputProps {
  className?: string
  hideLabel?: boolean
  description?: string
  label: string
  name: string
  prompt?: { link: string; label: string }
  validation?: ValidationOptions
}

export const NumberInputField = ({
  className,
  defaultValue = 0,
  hideLabel,
  value,
  prompt,
  description,
  label,
  name,
  validation,
  onValueChange,
  appearance,
  theme,
  ...remainingProps
}: NumberInputFieldProps) => {
  const { control } = useFormContext()
  const context = useFormCustomContext()
  const {
    field: { ref, onChange, value: innerValue, name: innerName }
  } = useController({
    name,
    control,
    rules: validation,
    defaultValue
  })
  const { error } = useFieldError(name)

  const formAppearance = context?.appearance || appearance
  const formTheme = context?.theme ?? theme

  React.useEffect(() => {
    // Update the react-hook-form inner value to match what is passed in.
    if (typeof value !== 'undefined') onChange(value)
  }, [value])

  return (
    <FieldWrapper
      className={className}
      description={description}
      error={error}
      fieldId={name}
      hideLabel={hideLabel}
      label={label}
      prompt={prompt}
      required={Boolean(validation?.required)}
      appearance={formAppearance}
    >
      <NumberInput
        id={name}
        name={innerName}
        ref={ref}
        {...(error && { state: 'error', 'aria-invalid': true })}
        defaultValue={defaultValue}
        onValueChange={(newValue) => {
          onChange(newValue)
          onValueChange?.(newValue)
        }}
        value={innerValue}
        appearance={formAppearance}
        theme={formTheme}
        {...remainingProps}
      />
    </FieldWrapper>
  )
}

NumberInputField.displayName = 'NumberInputField'
